/**
 * Générateur — Automatisme : exploiter le codage d'une figure pour identifier
 * des parallélogrammes particuliers.
 * Attendu ID : 1080 | Niveau : 5ème | Domaine : Parallélogrammes
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Quelle est sa nature ?     (QCM une figure + classer planche ①-④, carte
 *     FUSIONNÉE : même lecture de codage)
 *   (affirmer_tableau_vf RETIRÉ, revue 2026-09-02, décision prof.)
 *   - Quels codages porter ?     (QCM encoder : les codages nécessaires à
 *     une nature — le diagnostic « il manque / de trop » a été RETIRÉ,
 *     revue 2026-09-02, décision prof)
 *
 * ── Plan de cartes validé (2026-09-02) ─────────────────────────────────────
 * Source : Automatisme n°42 du doc 5e + grille P1-P12 (défrichage par
 * sous-agent, arbitrages prof du 2026-09-02).
 * DOCTRINE DE LA CARTE (P10) : « seuls les codages font foi ; ce qui n'est
 * pas codé ne se suppose jamais, ni dans un sens ni dans l'autre ». Un codage
 * manquant interdit de conclure ; un codage de trop fait conclure autre chose.
 * CODAGES DU CYCLE 3, et eux seuls : traits d'égalité (1 ou 2) et petit carré
 * d'angle droit. PROSCRITS : diagonales (objectif 5e non établi — limite de
 * la carte), flèches de parallélisme, mesures, quadrillage.
 * CADRAGE (arbitrage 2) : chaque figure est ANNONCÉE parallélogramme (c'est
 * l'intitulé : identifier des parallélogrammes PARTICULIERS) ; le codage
 * décide lequel — rectangle, losange, carré — ou « on ne peut rien affirmer
 * de plus » (formulation prof, 2026-09-02). Aucun théorème de 5e n'est jamais mobilisé.
 * VIVIER FERMÉ de motifs de codage (table relue à la main) : sont EXCLUS les
 * motifs dont la vérité mathématique dépasse la lecture cycle 3 dans un
 * parallélogramme — 2 côtés consécutifs égaux (⟹ losange), 3 côtés égaux,
 * angles droits partiels (1 angle droit ⟹ rectangle), rectangle + 2 côtés
 * consécutifs égaux (arbitrage 3 : exclu). Garde par throw.
 * ALLURE (arbitrage 1) : le dessin ne contredit JAMAIS le codage (vérifié en
 * flottant, throw), mais il peut posséder des propriétés non codées (allure
 * trompeuse : un vrai carré codé seulement rectangle → réponse « rectangle »)
 * et il est penché au hasard (le geste de la carte).
 * INVARIANTS : conclusion du vivier === lecteur de codage (fonction pure,
 * double dérivation, throw) ; nature géométrique du dessin au moins aussi
 * précise que la conclusion ; SVG avec attributs machine (data-figure,
 * data-code) pour les solveurs ; pas de calculatrice.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeClasser,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';

const ATTENDU_ID = 1080;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_NATURE = 'Quelle est sa nature ?';
const L_CODER = 'Quels codages porter ?';

// ─── Le lecteur de codage (règles cycle 3, fonction pure) ──────────────────
// marques[i] ∈ {0, 1, 2} pour les côtés AB, BC, CD, DA ; droits = sommets codés.

const lireCodage = (marques, droits) => {
    const losange = marques.every((m) => m > 0 && m === marques[0]);
    const rectangle = droits.length === 4;
    if (losange && rectangle) return 'carre';
    if (rectangle) return 'rectangle';
    if (losange) return 'losange';
    return 'sansplus';
};

const NOMS_CONCLUSIONS = {
    carre: 'un carré',
    rectangle: 'un rectangle',
    losange: 'un losange',
    sansplus: 'on ne peut rien affirmer de plus',
};

// Vivier FERMÉ (table §3.3 du défrichage, motifs C10/C12/C13-C19 exclus).
const MOTIFS_1080 = [
    { id: 'C1', marques: [0, 0, 0, 0], droits: [], conclusion: 'sansplus' },
    { id: 'C2', marques: [1, 2, 1, 2], droits: [], conclusion: 'sansplus' },
    { id: 'C3', marques: [0, 0, 0, 0], droits: [0, 1, 2, 3], conclusion: 'rectangle' },
    { id: 'C4', marques: [1, 0, 0, 0], droits: [0, 1, 2, 3], conclusion: 'rectangle' },
    { id: 'C5', marques: [1, 2, 1, 2], droits: [0, 1, 2, 3], conclusion: 'rectangle' },
    { id: 'C6', marques: [1, 1, 1, 1], droits: [], conclusion: 'losange' },
    { id: 'C7', marques: [1, 1, 1, 1], droits: [0, 1, 2, 3], conclusion: 'carre' },
    { id: 'C9', marques: [1, 0, 0, 0], droits: [], conclusion: 'sansplus' },
    { id: 'C11', marques: [1, 0, 1, 0], droits: [], conclusion: 'sansplus' },
];

/** Garde du vivier : aucun motif « zone ambiguë » ne peut exister. */
MOTIFS_1080.forEach((m) => {
    if (m.droits.length !== 0 && m.droits.length !== 4) throw new Error(`gen_1080 : motif ${m.id} — angles droits partiels interdits`);
    const groupes = {};
    m.marques.forEach((mk, i) => { if (mk > 0) (groupes[mk] = groupes[mk] || []).push(i); });
    Object.values(groupes).forEach((idx) => {
        if (idx.length === 3) throw new Error(`gen_1080 : motif ${m.id} — trois côtés égaux interdits`);
        if (idx.length === 2 && (idx[1] - idx[0]) % 2 !== 0) throw new Error(`gen_1080 : motif ${m.id} — deux côtés consécutifs égaux interdits`);
    });
    if (lireCodage(m.marques, m.droits) !== m.conclusion) throw new Error(`gen_1080 : motif ${m.id} — conclusion contredite par le lecteur`);
});

const motifParId = (id) => MOTIFS_1080.find((m) => m.id === id);

/** Décale cycliquement un motif (le trait isolé change de côté, etc.). */
const decalerMotif = (motif, s) => ({
    ...motif,
    marques: motif.marques.map((_, i) => motif.marques[(i - s + 4) % 4]),
    droits: motif.droits.map((d) => (d + s) % 4),
});

// ─── Géométrie du dessin : familles, rotation, vérification ────────────────

const FAMILLES = {
    carre: () => { const a = 4; return [[0, 0], [a, 0], [a, a], [0, a]]; },
    rectangle: () => {
        const a = 3.2; const b = a * (1.35 + Math.random() * 0.45);
        return Math.random() < 0.5 ? [[0, 0], [b, 0], [b, a], [0, a]] : [[0, 0], [a, 0], [a, b], [0, b]];
    },
    losange: () => {
        const p = 2.8; const q = p * (1.45 + Math.random() * 0.4);
        return Math.random() < 0.5 ? [[-p, 0], [0, -q], [p, 0], [0, q]] : [[-q, 0], [0, -p], [q, 0], [0, p]];
    },
    parallelogramme: () => {
        const u = [4.6, 0]; const v = [1.4 + Math.random() * 0.8, 2.6 + Math.random() * 0.5];
        return [[0, 0], u, [u[0] + v[0], u[1] + v[1]], v];
    },
};

const vec = (P, Q) => [Q[0] - P[0], Q[1] - P[1]];
const dot = (u, v) => u[0] * v[0] + u[1] * v[1];
const norme = (u) => Math.hypot(u[0], u[1]);

/** Nature géométrique du dessin (tolérance flottante), pour la garde. */
const natureDessin = (pts) => {
    const c = [vec(pts[0], pts[1]), vec(pts[1], pts[2]), vec(pts[2], pts[3]), vec(pts[3], pts[0])];
    const eq = (a, b) => Math.abs(norme(a) - norme(b)) < 1e-6 * Math.max(norme(a), norme(b));
    const droit = (a, b) => Math.abs(dot(a, b)) < 1e-6 * norme(a) * norme(b);
    const par = (a, b) => Math.abs(a[0] * b[1] - a[1] * b[0]) < 1e-6 * norme(a) * norme(b);
    if (!(par(c[0], c[2]) && par(c[1], c[3]))) return 'quelconque';
    const losange = eq(c[0], c[1]);
    const rectangle = droit(c[0], c[1]);
    if (losange && rectangle) return 'carre';
    if (rectangle) return 'rectangle';
    if (losange) return 'losange';
    return 'parallelogramme';
};

const tourner = (pts, deg) => {
    const a = (deg * Math.PI) / 180; const co = Math.cos(a); const si = Math.sin(a);
    return pts.map(([x, y]) => [x * co - y * si, x * si + y * co]);
};

/** Le dessin respecte-t-il chaque codage ? (égalités exactes, angles droits exacts) */
const verifierCodageDessin = (pts, motif) => {
    const c = [vec(pts[0], pts[1]), vec(pts[1], pts[2]), vec(pts[2], pts[3]), vec(pts[3], pts[0])];
    for (let i = 0; i < 4; i++) {
        for (let j = i + 1; j < 4; j++) {
            if (motif.marques[i] > 0 && motif.marques[i] === motif.marques[j]
                && Math.abs(norme(c[i]) - norme(c[j])) > 1e-6 * norme(c[i])) {
                throw new Error('gen_1080 : un codage d\'égalité contredit le dessin');
            }
        }
    }
    motif.droits.forEach((s) => {
        const entrant = c[(s + 3) % 4]; const sortant = c[s];
        if (Math.abs(dot(entrant, sortant)) > 1e-6 * norme(entrant) * norme(sortant)) {
            throw new Error('gen_1080 : un codage d\'angle droit contredit le dessin');
        }
    });
};

/**
 * Famille de dessin pour une conclusion : allure fidèle (propriétés non
 * codées visiblement fausses) ou trompeuse (arbitrage 1). Toujours un
 * parallélogramme (la figure est annoncée telle).
 */
const familleDessin = (conclusion, trompeuse) => {
    if (conclusion === 'carre') return 'carre';
    if (conclusion === 'rectangle') return trompeuse ? 'carre' : 'rectangle';
    if (conclusion === 'losange') return trompeuse ? 'carre' : 'losange';
    return trompeuse ? randomChoice(['rectangle', 'losange', 'carre']) : 'parallelogramme';
};

const PRECISION = { sansplus: 0, rectangle: 1, losange: 1, carre: 2 };
const NATURE_DESSIN_ADMISE = {
    sansplus: ['parallelogramme', 'rectangle', 'losange', 'carre'],
    rectangle: ['rectangle', 'carre'],
    losange: ['losange', 'carre'],
    carre: ['carre'],
};

/** Construit une figure codée : coordonnées + motif + allure, toutes gardes passées. */
const construireFigure = (motifId, forcerFamille = null) => {
    const base = motifParId(motifId);
    const motif = decalerMotif(base, randInt(0, 3));
    const trompeuse = Math.random() < 0.35;
    const famille = forcerFamille || familleDessin(motif.conclusion, trompeuse);
    const rotation = Math.random() < 0.35 ? 0 : randInt(5, 355);
    const pts = tourner(FAMILLES[famille](), rotation);
    verifierCodageDessin(pts, motif);
    const nature = natureDessin(pts);
    if (!NATURE_DESSIN_ADMISE[motif.conclusion].includes(nature)) {
        throw new Error(`gen_1080 : dessin ${nature} incompatible avec la conclusion ${motif.conclusion}`);
    }
    return { pts, motif, famille, rotation, trompeuse, natureDessin: nature };
};

// ─── Rendu SVG : polygone, traits, petits carrés, étiquettes, données ───────

const NOMS_SOMMETS = ['A', 'B', 'C', 'D'];

/** Contenu SVG (sans balise racine) d'une figure codée, dans un cadre donné. */
const dessinerFigure = (fig, cadre, noms = NOMS_SOMMETS) => {
    const { pts, motif } = fig;
    const xs = pts.map((p) => p[0]); const ys = pts.map((p) => p[1]);
    const xmin = Math.min(...xs); const xmax = Math.max(...xs);
    const ymin = Math.min(...ys); const ymax = Math.max(...ys);
    const marge = 30;
    const echelle = Math.min((cadre.w - 2 * marge) / (xmax - xmin), (cadre.h - 2 * marge) / (ymax - ymin));
    const X = (x) => cadre.x + marge + (x - xmin) * echelle + ((cadre.w - 2 * marge) - (xmax - xmin) * echelle) / 2;
    const Y = (y) => cadre.y + marge + (ymax - y) * echelle + ((cadre.h - 2 * marge) - (ymax - ymin) * echelle) / 2;
    const P = pts.map((p) => [X(p[0]), Y(p[1])]);
    const cx = P.reduce((s, p) => s + p[0], 0) / 4; const cy = P.reduce((s, p) => s + p[1], 0) / 4;

    let s = `<path d="M ${P[0][0].toFixed(1)} ${P[0][1].toFixed(1)} L ${P[1][0].toFixed(1)} ${P[1][1].toFixed(1)} L ${P[2][0].toFixed(1)} ${P[2][1].toFixed(1)} L ${P[3][0].toFixed(1)} ${P[3][1].toFixed(1)} Z" fill="#eff6ff" stroke="#1E3A8A" stroke-width="2.2"/>`;
    // Traits d'égalité (1 ou 2), perpendiculaires au milieu du côté.
    for (let i = 0; i < 4; i++) {
        const n = motif.marques[i];
        if (!n) continue;
        const A = P[i]; const B = P[(i + 1) % 4];
        const mx = (A[0] + B[0]) / 2; const my = (A[1] + B[1]) / 2;
        const d = [B[0] - A[0], B[1] - A[1]]; const L = norme(d);
        const ux = d[0] / L; const uy = d[1] / L;      // le long du côté
        const nx = -uy; const ny = ux;                 // perpendiculaire
        for (let t = 0; t < n; t++) {
            const off = (t - (n - 1) / 2) * 5;
            const px = mx + ux * off; const py = my + uy * off;
            s += `<line x1="${(px - nx * 6).toFixed(1)}" y1="${(py - ny * 6).toFixed(1)}" x2="${(px + nx * 6).toFixed(1)}" y2="${(py + ny * 6).toFixed(1)}" stroke="#111827" stroke-width="2" data-code="tick" data-cote="${i}" data-n="${n}"/>`;
        }
    }
    // Petits carrés d'angle droit, à l'intérieur de l'angle.
    motif.droits.forEach((v) => {
        const S = P[v]; const prev = P[(v + 3) % 4]; const next = P[(v + 1) % 4];
        const a = vec(S, prev); const b = vec(S, next);
        const ua = [a[0] / norme(a), a[1] / norme(a)]; const ub = [b[0] / norme(b), b[1] / norme(b)];
        const k = 11;
        const p1 = [S[0] + ua[0] * k, S[1] + ua[1] * k];
        const p2 = [S[0] + (ua[0] + ub[0]) * k, S[1] + (ua[1] + ub[1]) * k];
        const p3 = [S[0] + ub[0] * k, S[1] + ub[1] * k];
        s += `<polyline points="${p1[0].toFixed(1)},${p1[1].toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)} ${p3[0].toFixed(1)},${p3[1].toFixed(1)}" fill="none" stroke="#111827" stroke-width="1.8" data-code="carre" data-sommet="${v}"/>`;
    });
    // Étiquettes poussées vers l'extérieur depuis le centre.
    P.forEach((p, i) => {
        const d = [p[0] - cx, p[1] - cy]; const L = norme(d) || 1;
        const lx = p[0] + (d[0] / L) * 15; const ly = p[1] + (d[1] / L) * 15 + 5;
        s += `<circle cx="${p[0].toFixed(1)}" cy="${p[1].toFixed(1)}" r="2.4" fill="#1e293b"/>`
            + `<text x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">${noms[i]}</text>`;
    });
    return s;
};

// 6 décimales : les solveurs recalculent égalités et angles droits sur ces
// coordonnées, 3 décimales laissaient un bruit d'arrondi de l'ordre de 1e-3.
const dataFigure = (fig, noms = NOMS_SOMMETS) => fig.pts.map((p, i) => `${noms[i]}:${p[0].toFixed(6)},${p[1].toFixed(6)}`).join('|');

const svgFigure = (fig) => {
    const w = 320; const h = 250;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" font-family="Arial" data-figure="${dataFigure(fig)}" data-marques="${fig.motif.marques.join(',')}" data-droits="${fig.motif.droits.join(',')}">${dessinerFigure(fig, { x: 0, y: 0, w, h })}</svg>`;
};

/** Planche de 4 figures ① à ④ (réplique de la série flash). */
const svgPlanche = (figs) => {
    const w = 190; const h = 200; const numeros = ['①', '②', '③', '④'];
    let s = '';
    figs.forEach((fig, i) => {
        const x = i * w;
        s += `<g data-index="${i + 1}" data-marques="${fig.motif.marques.join(',')}" data-droits="${fig.motif.droits.join(',')}">`
            + `<text x="${x + w / 2}" y="22" font-size="18" font-weight="800" fill="#1E3A8A" text-anchor="middle">${numeros[i]}</text>`
            + dessinerFigure(fig, { x, y: 26, w, h: h - 26 }) + '</g>';
    });
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w * 4} ${h}" width="${w * 4}" height="${h}" font-family="Arial">${s}</svg>`;
};

// ─── Format 1a : QCM nature (une figure) ───────────────────────────────────

// Anti-répétition immédiate (recette standard).
let derniereSignatureNature1080 = null;

const genNatureQcm = () => {
    let payload = construireNatureQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureNature1080; t++) {
        payload = construireNatureQcm();
    }
    derniereSignatureNature1080 = payload._meta.params.signature;
    return payload;
};

const construireNatureQcm = () => {
    // La CONCLUSION est tirée d'abord (4 natures équiprobables), puis un motif
    // de cette nature : sinon « rien de plus » (4 motifs sur 9) raflait 45 % des
    // tirages — et c'est l'option la plus longue (revue 2026-09-02, A2).
    const conclusion = randomChoice(Object.keys(NOMS_CONCLUSIONS));
    const fig = construireFigure(randomChoice(MOTIFS_1080.filter((m) => m.conclusion === conclusion)).id);
    const correct = NOMS_CONCLUSIONS[fig.motif.conclusion];
    const autres = Object.keys(NOMS_CONCLUSIONS).filter((c) => c !== fig.motif.conclusion).map((c) => NOMS_CONCLUSIONS[c]);
    const texte = '<p>ABCD est un parallélogramme.</p>'
        + '<p>D\'après son codage, quelle est sa nature la plus précise ?</p>';
    const payload = makeQcm([correct, ...autres], correct, texte, {
        ...opts(L_NATURE), elements_additionnels: imgElement(svgFigure(fig), 'medium'),
    });
    payload._meta.params = {
        motif: fig.motif.id, conclusion: fig.motif.conclusion, famille: fig.famille,
        trompeuse: fig.trompeuse, rotation: fig.rotation, signature: `${fig.motif.id}-${fig.famille}`,
    };
    return payload;
};

// ─── Format 1b : classer une planche de 4 figures ──────────────────────────

// Anti-répétition immédiate (recette standard).
let derniereSignatureClasser1080 = null;

const genNatureClasser = () => {
    let payload = construireNatureClasser();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureClasser1080; t++) {
        payload = construireNatureClasser();
    }
    derniereSignatureClasser1080 = payload._meta.params.signature;
    return payload;
};

const construireNatureClasser = () => {
    for (let essai = 0; essai < 40; essai++) {
        // Une figure PAR nature (aucune catégorie vide, comme le classer du
        // 1081) : le motif de chaque nature est tiré dans son sous-vivier.
        const ids = shuffle(Object.keys(NOMS_CONCLUSIONS).map((c) => randomChoice(MOTIFS_1080.filter((m) => m.conclusion === c)).id));
        const figs = ids.map((id) => construireFigure(id));
        const conclusions = figs.map((f) => f.motif.conclusion);
        if (new Set(conclusions).size !== 4) continue;
        const categories = Object.keys(NOMS_CONCLUSIONS).map((c) => ({
            name: NOMS_CONCLUSIONS[c].charAt(0).toUpperCase() + NOMS_CONCLUSIONS[c].slice(1),
            items: figs.map((f, i) => (f.motif.conclusion === c ? `Figure ${i + 1}` : null)).filter(Boolean),
        }));
        const payload = makeClasser(
            categories,
            '<p>Les quatre figures sont des parallélogrammes. Classe chacune d\'après son codage.</p>',
            { ...opts(L_NATURE), elements_additionnels: imgElement(svgPlanche(figs), 'large') },
        );
        payload._meta.params = { motifs: ids, conclusions, signature: ids.join('-') };
        return payload;
    }
    throw new Error('gen_1080 : planche sans mixité de natures après 40 essais');
};

// (affirmer_tableau_vf RETIRÉ, revue 2026-09-02, décision prof.)

// ─── Format 3 : quels codages porter ? ─────────────────────────────────
// (defaut_qcm_unique RETIRÉ, revue 2026-09-02, décision prof.)

const NOMS_INTENTIONS = { rectangle: 'un rectangle', losange: 'un losange', carre: 'un carré' };


// Options à longueur homogène (60-68 caractères, revue 2026-09-02, A2) et
// vivier élargi (A8). Chaque option porte son codage EXPLICITE : sa
// conclusion est recalculée par le lecteur, jamais déclarée à la main.
const OPTIONS_CODER = [
    { txt: 'Le même trait sur chacun des quatre côtés, sans aucun angle codé.', marques: [1, 1, 1, 1], droits: [] },
    { txt: 'Deux traits sur chacun des quatre côtés, et aucun angle droit codé.', marques: [2, 2, 2, 2], droits: [] },
    { txt: 'Un petit carré d\'angle droit à chaque sommet, et aucun trait codé.', marques: [0, 0, 0, 0], droits: [0, 1, 2, 3] },
    { txt: 'Même trait sur les quatre côtés et un petit carré à chaque sommet.', marques: [1, 1, 1, 1], droits: [0, 1, 2, 3] },
    { txt: 'Quatre traits identiques et quatre petits carrés d\'angle droit.', marques: [1, 1, 1, 1], droits: [0, 1, 2, 3] },
    { txt: 'Deux traits sur chaque côté, et un petit carré à chacun des sommets.', marques: [2, 2, 2, 2], droits: [0, 1, 2, 3] },
    { txt: 'Un trait sur [AB] et [DC], deux traits sur [AD] et [BC], aucun angle.', marques: [1, 2, 1, 2], droits: [] },
    { txt: 'Deux traits sur [AB] et [DC], un trait sur [AD] et [BC], aucun angle.', marques: [2, 1, 2, 1], droits: [] },
    { txt: 'Un trait sur [AB] seulement, et un petit carré à chacun des sommets.', marques: [1, 0, 0, 0], droits: [0, 1, 2, 3], leurreSeulement: true },
    { txt: 'Un trait sur [AB] seulement, et aucun autre codage sur la figure.', marques: [1, 0, 0, 0], droits: [] },
    { txt: 'Un trait sur [BC] seulement, et aucun autre codage sur la figure.', marques: [0, 1, 0, 0], droits: [] },
];
OPTIONS_CODER.forEach((o) => { o.conclusion = lireCodage(o.marques, o.droits); });

// Anti-répétition immédiate (recette standard).
let derniereSignatureCoder1080 = null;

const genCoderQcm = () => {
    let payload = construireCoderQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureCoder1080; t++) {
        payload = construireCoderQcm();
    }
    derniereSignatureCoder1080 = payload._meta.params.signature;
    return payload;
};

const construireCoderQcm = () => {
    const cible = randomChoice(['rectangle', 'losange', 'carre']);
    const conclusionDe = (o) => o.conclusion;
    // Jamais une option à codage INUTILE comme bonne réponse (revue 2026-09-02).
    const bonne = randomChoice(OPTIONS_CODER.filter((o) => conclusionDe(o) === cible && !o.leurreSeulement));
    const leurres = shuffle(OPTIONS_CODER.filter((o) => conclusionDe(o) !== cible)).slice(0, 3);
    if (!bonne || leurres.length < 3) throw new Error('gen_1080 : options de codage insuffisantes');
    // Garde : exactement UNE option retenue conclut à la cible.
    const retenues = [bonne, ...leurres];
    if (retenues.filter((o) => conclusionDe(o) === cible).length !== 1) throw new Error('gen_1080 : deux options concluent à la cible');

    // Figure de référence : le parallélogramme ABCD, sans aucun codage.
    const fig = construireFigure('C1', 'parallelogramme');
    const texte = `<p>ABCD est un parallélogramme. Quels codages faut-il porter sur la figure pour pouvoir affirmer que c'est ${NOMS_INTENTIONS[cible]} ?</p>`;
    const payload = makeQcm(retenues.map((o) => o.txt), bonne.txt, texte, {
        ...opts(L_CODER), elements_additionnels: imgElement(svgFigure(fig), 'medium'),
    });
    payload._meta.params = { cible, signature: `${cible}-${bonne.txt.slice(0, 12)}-${leurres.map((o) => o.txt.slice(0, 12)).sort().join('')}` };
    return payload;
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    nature_qcm_unique: genNatureQcm,
    nature_classer: genNatureClasser,
    coder_qcm_unique: genCoderQcm,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', classer: 'Classer', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'nature', label: L_NATURE, formatsList: buildFormats('nature') },
    { id: 'coder', label: L_CODER, formatsList: buildFormats('coder') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
